import { useEffect, useMemo, useState } from "react";
import { Plus, RefreshCw, Trash2, Copy, Loader2, AlertTriangle, Eye, Image as ImageIcon, Video, FileText, MapPin } from "lucide-react";
import { toast } from "react-toastify";
import { listTemplates, syncTemplates, deleteTemplate } from "../../services/templatesApi";
import TemplateEditor from "./TemplateEditor";
import TemplateViewModal from "./TemplateViewModal";
import { STATUS_BADGES, CATEGORY_LABEL, findComponent, getBodyText } from "./templateMeta";

const HEADER_ICON = { IMAGE: ImageIcon, VIDEO: Video, DOCUMENT: FileText, LOCATION: MapPin };

// Mais novo primeiro: o id é atribuído na primeira vez que o template é visto
// (criação local ou 1º sync) e o upsert preserva — é a melhor proxy de "ordem de criação".
function sortNewestFirst(list) {
  return [...list].sort((a, b) => (b.id || 0) - (a.id || 0));
}

export default function TemplatesList({ contaId, socket }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const [filterStatus, setFilterStatus] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterLang, setFilterLang] = useState("");
  const [search, setSearch] = useState("");

  const [editor, setEditor] = useState(null);
  const [viewing, setViewing] = useState(null);

  async function carregar() {
    setLoading(true);
    try {
      const data = await listTemplates(contaId, {
        status: filterStatus || undefined,
        category: filterCategory || undefined,
        language: filterLang || undefined,
      });
      setRows(sortNewestFirst(data));
    } catch (err) {
      toast.error("Falha ao carregar templates: " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    carregar();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contaId, filterStatus, filterCategory, filterLang]);

  useEffect(() => {
    if (!socket || !contaId) return;
    socket.emit("join-templates", contaId);
    const handler = (payload) => {
      if (!payload || payload.conta_id !== contaId) return;
      setRows((prev) => {
        const i = prev.findIndex((r) =>
          (payload.meta_template_id && r.meta_template_id === payload.meta_template_id) ||
          (r.name === payload.name && r.language === payload.language)
        );
        if (i === -1) return prev;
        const updated = [...prev];
        updated[i] = {
          ...updated[i],
          status: payload.status,
          rejection_reason: payload.rejection_reason ?? updated[i].rejection_reason,
          quality_score: payload.quality_score ?? updated[i].quality_score,
          category: payload.category ?? updated[i].category,
          meta_template_id: payload.meta_template_id ?? updated[i].meta_template_id,
        };
        return updated;
      });
      const badge = STATUS_BADGES[payload.status]?.label || payload.status;
      toast.info(`Template "${payload.name}" agora está: ${badge}`);
    };
    socket.on("template:status", handler);
    return () => {
      socket.off("template:status", handler);
      socket.emit("leave-templates", contaId);
    };
  }, [socket, contaId]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      r.name.toLowerCase().includes(q) || getBodyText(r.components).toLowerCase().includes(q)
    );
  }, [rows, search]);

  async function handleSync() {
    setSyncing(true);
    try {
      const r = await syncTemplates(contaId);
      toast.success(`Sync concluído: ${r.upserted} atualizados, ${r.removed} removidos`);
      await carregar();
    } catch (err) {
      toast.error("Falha no sync: " + (err.response?.data?.message || err.message));
    } finally {
      setSyncing(false);
    }
  }

  async function handleDelete(row) {
    if (!row.meta_template_id) {
      toast.error("Template sem meta_template_id — re-sincronize antes");
      return;
    }
    if (!window.confirm(`Excluir o template "${row.name}" (${row.language})?\nA Meta também removerá.`)) {
      return;
    }
    try {
      await deleteTemplate(contaId, row.meta_template_id);
      toast.success("Template removido");
      setRows((prev) => prev.filter((r) => r.meta_template_id !== row.meta_template_id));
    } catch (err) {
      toast.error("Falha ao remover: " + (err.response?.data?.message || err.message));
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="text-lg font-semibold text-white">Templates</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={handleSync}
            disabled={syncing}
            className="inline-flex items-center gap-2 bg-surface-2 hover:bg-surface-3 disabled:bg-surface-1 text-white px-3 py-1.5 rounded text-sm font-medium transition"
          >
            {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Atualizar da Meta
          </button>
          <button
            onClick={() => setEditor("new")}
            className="inline-flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white px-3 py-1.5 rounded text-sm font-medium transition"
          >
            <Plus className="w-4 h-4" /> Novo template
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nome ou texto da mensagem..."
          className="flex-1 min-w-[180px] bg-base border border-white/[0.06] rounded text-sm text-ink-2 px-3 py-1.5 placeholder:text-ink-5"
        />
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="bg-base border border-white/[0.06] rounded text-sm text-ink-2 px-2 py-1.5">
          <option value="">Todos status</option>
          {Object.entries(STATUS_BADGES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} className="bg-base border border-white/[0.06] rounded text-sm text-ink-2 px-2 py-1.5">
          <option value="">Todas categorias</option>
          {Object.entries(CATEGORY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={filterLang} onChange={(e) => setFilterLang(e.target.value)} className="bg-base border border-white/[0.06] rounded text-sm text-ink-2 px-2 py-1.5">
          <option value="">Todos idiomas</option>
          <option value="pt_BR">pt_BR</option>
          <option value="pt_PT">pt_PT</option>
          <option value="en">en</option>
          <option value="en_US">en_US</option>
          <option value="es">es</option>
        </select>
      </div>

      {loading ? (
        <div className="bg-surface-1 border border-white/[0.06] rounded-lg p-12 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-ink-4" />
        </div>
      ) : visible.length === 0 ? (
        <div className="bg-surface-1 border border-white/[0.06] rounded-lg p-8 text-center text-sm text-ink-4">
          Nenhum template encontrado{rows.length > 0 ? " com esses filtros" : ""}
        </div>
      ) : (
        <div className="bg-surface-1 border border-white/[0.06] rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-base text-xs text-ink-4 uppercase">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Nome</th>
                <th className="text-left px-4 py-2 font-medium w-[38%]">Mensagem</th>
                <th className="text-left px-4 py-2 font-medium">Idioma</th>
                <th className="text-left px-4 py-2 font-medium">Categoria</th>
                <th className="text-left px-4 py-2 font-medium">Status</th>
                <th className="text-right px-4 py-2 font-medium">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.06]">
              {visible.map((r) => {
                const badge = STATUS_BADGES[r.status] || STATUS_BADGES.DISABLED;
                const bodyText = getBodyText(r.components);
                const headerFmt = findComponent(r.components, "HEADER")?.format;
                const HeaderIcon = headerFmt ? HEADER_ICON[headerFmt] : null;
                return (
                  <tr
                    key={`${r.name}::${r.language}`}
                    className="hover:bg-base/60 cursor-pointer"
                    onClick={() => setViewing(r)}
                    title="Clique pra ver a mensagem completa"
                  >
                    <td className="px-4 py-2 font-mono text-ink-2 whitespace-nowrap">{r.name}</td>
                    <td className="px-4 py-2 text-ink-3">
                      <div className="flex items-start gap-2 min-w-0">
                        {HeaderIcon && <HeaderIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-ink-4" title={headerFmt} />}
                        <span className="line-clamp-2 break-words leading-snug">
                          {bodyText || <span className="text-ink-5 italic">(sem body)</span>}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-2 text-ink-3">{r.language}</td>
                    <td className="px-4 py-2 text-ink-3">{CATEGORY_LABEL[r.category] || r.category}</td>
                    <td className="px-4 py-2">
                      <span
                        className={`text-xs font-medium border rounded px-2 py-0.5 ${badge.cls}`}
                        title={r.rejection_reason || ""}
                      >
                        {badge.label}
                      </span>
                      {r.status === "REJECTED" && r.rejection_reason && (
                        <span className="ml-2 inline-flex items-center text-xs text-red-400" title={r.rejection_reason}>
                          <AlertTriangle className="w-3 h-3" />
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => setViewing(r)}
                        className="text-ink-3 hover:text-brand-300 inline-flex items-center gap-1 text-xs px-2 py-1"
                        title="Ver mensagem completa"
                      >
                        <Eye className="w-3.5 h-3.5" /> Ver
                      </button>
                      <button
                        onClick={() => setEditor({ isDuplicate: true, source: r })}
                        className="text-ink-3 hover:text-brand-300 inline-flex items-center gap-1 text-xs px-2 py-1"
                        title="Duplicar como novo template"
                      >
                        <Copy className="w-3.5 h-3.5" /> Duplicar
                      </button>
                      <button
                        onClick={() => handleDelete(r)}
                        className="text-ink-3 hover:text-red-400 inline-flex items-center gap-1 text-xs px-2 py-1"
                        title="Excluir template"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {viewing && (
        <TemplateViewModal
          row={viewing}
          onClose={() => setViewing(null)}
          onDuplicate={(r) => {
            setViewing(null);
            setEditor({ isDuplicate: true, source: r });
          }}
        />
      )}

      {editor && (
        <TemplateEditor
          contaId={contaId}
          isDuplicate={editor !== "new" ? editor.isDuplicate : false}
          source={editor !== "new" ? editor.source : null}
          onClose={() => setEditor(null)}
          onSaved={() => {
            setEditor(null);
            carregar();
          }}
        />
      )}
    </div>
  );
}
